'use client';
import { get } from 'lodash';
import { useId, type ComponentProps } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { FormFieldMessage } from '../message';

import CheckboxGroup from './checkbox-group';

import type { FieldValues, Path, PathValue } from 'react-hook-form';

type CheckboxFieldProps<T> = {
  name: Path<T>;
  disabled?: boolean;
  required?: boolean;
  showHeading?: boolean;
  defaultValue?: PathValue<T, Path<T>>;
  onPreferenceSelected?: (value: string) => void;
  onPreferenceDeSelected?: (value: string) => void;
} & Pick<
  ComponentProps<typeof CheckboxGroup>,
  'options' | 'heading' | 'variant'
> &
  Pick<ComponentProps<typeof FormFieldMessage>, 'hint'>;

const CheckboxField = <T extends FieldValues>({
  name,
  options,
  heading,
  defaultValue,
  disabled = false,
  hint,
  variant,
  showHeading,
  required,
  onPreferenceDeSelected,
  onPreferenceSelected,
}: CheckboxFieldProps<T>) => {
  const id = useId();
  const {
    control,
    formState: { errors },
  } = useFormContext<T>();

  const error = get(errors, name);

  const { field } = useController({
    control,
    name,
    disabled,
    defaultValue,
  });
  const messageId = `checkbox-message-${id}`;

  return (
    <div className="flex flex-col gap-d">
      <CheckboxGroup
        {...field}
        variant={variant}
        onChange={(val) => {
          let newArr = [...field.value];
          if (newArr.includes(val)) {
            newArr = newArr.filter((v) => v !== val);
            onPreferenceDeSelected?.(val as string);
          } else {
            newArr.push(val);
            onPreferenceSelected?.(val as string);
          }
          field.onChange(newArr);
        }}
        heading={heading}
        options={options}
        checked={field.value}
        required={required}
        showHeading={showHeading}
        hasError={Boolean(error)}
      />
      <FormFieldMessage
        id={messageId}
        hint={hint}
        error={error?.message as string}
      />
    </div>
  );
};

export default CheckboxField;
